import clsx from 'clsx';
import {
  Control,
  FieldError,
  FieldPath,
  FieldPathValue,
  FieldValues,
  get,
  useController,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Button from '../Button';
import FormError from '../FormError';
import FormLabel from '../FormLabel';

interface Props {
  className?: string;
  disabled?: boolean;
  onChange?: (value: boolean) => void;
  value?: boolean;
}

const YesNoInput = function YesNoInput({
  className,
  disabled,
  onChange,
  value,
}: Props): JSX.Element {
  const { t } = useTranslation();

  return (
    <div className={clsx('flex space-x-2', className)}>
      <Button
        size="large"
        color={value === true ? 'primary' : 'white'}
        className="flex-1 justify-center"
        onClick={() => onChange?.(true)}
        disabled={disabled}
      >
        {t('buttons.yes', 'Yes')}
      </Button>
      <Button
        size="large"
        color={value === false ? 'primary' : 'white'}
        className="flex-1 justify-center"
        onClick={() => onChange?.(false)}
        disabled={disabled}
      >
        {t('buttons.no', 'No')}
      </Button>
    </div>
  );
};

interface YesNoInputLabelledProps extends Props {
  label?: React.ReactNode;
  error?: React.ReactNode;
}

YesNoInput.Labelled = function TextInputLabelled({
  label,
  className,
  error,
  ...rest
}: YesNoInputLabelledProps): JSX.Element {
  return (
    // eslint-disable-next-line jsx-a11y/label-has-associated-control
    <div className={className}>
      {label && <FormLabel>{label}</FormLabel>}
      <YesNoInput {...rest} />
      {error && <FormError>{error}</FormError>}
    </div>
  );
};

interface YesNoInputControllerProps<
  TFieldValues extends FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends YesNoInputLabelledProps {
  control: Control<TFieldValues>;
  name: TFieldName;
}

YesNoInput.LabelledController = function YesNoInputController<
  TFieldValues extends FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  ...rest
}: YesNoInputControllerProps<TFieldValues, TFieldName>): JSX.Element {
  const {
    field: { value, onChange },
    formState: { errors },
  } = useController({ control, name });
  const error = get(errors, name) as FieldError | undefined;

  return (
    <YesNoInput.Labelled
      onChange={(val) =>
        onChange(val as FieldPathValue<TFieldValues, TFieldName>)
      }
      value={value as boolean}
      error={error?.message}
      {...rest}
    />
  );
};

export default YesNoInput;
